import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import usePersistentState from '../utils/usePersistentState';
import toast from 'react-hot-toast';
import { authAPI } from '../utils/api';
import { Button, Input, Select, Modal, Badge, EmptyState, Pagination, Spinner, Card } from '../components/UI';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const roleBadge = { SuperAdmin: 'purple', RecruiterManager: 'info', Recruiter: 'success', Developer: 'warning' };

// Solid action button used in the row Actions column. White text on the
// action's accent color so each pill reads as an obvious button regardless
// of theme. Hover darkens slightly + tiny lift for tactile feedback.
function ActionPill({ color, onClick, children, title }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '6px 12px',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.2,
        color: '#ffffff',
        background: color,
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        boxShadow: hover
          ? `0 4px 12px ${color}55, 0 1px 2px rgba(0,0,0,0.08)`
          : `0 1px 2px ${color}33, 0 1px 1px rgba(0,0,0,0.04)`,
        transform: hover ? 'translateY(-1px)' : 'none',
        transition: 'transform 0.12s, box-shadow 0.15s, filter 0.15s',
        filter: hover ? 'brightness(1.06)' : 'none',
        whiteSpace: 'nowrap',
        letterSpacing: '0.01em',
      }}
    >
      {children}
    </button>
  );
}

export default function Users() {
  const [users, setUsers]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [page, setPage]         = useState(1);
  // Kept for this tab — survives leaving the page and coming back.
  const [roleFilter, setRoleFilter] = usePersistentState('users:role', '');
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm]         = useState({ name: '', email: '', password: '', role: 'Recruiter', isActive: true });
  const [saving, setSaving]         = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // user to confirm delete
  const [deleting, setDeleting]     = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user: currentUser, isSuperAdmin, startImpersonation, impersonatedAs } = useAuth();
  const [impersonateTarget, setImpersonateTarget] = useState(null); // user to start impersonating
  const [impersonateReason, setImpersonateReason] = useState('');
  const [startingImpersonation, setStartingImpersonation] = useState(false);

  // Grants the CURRENT user holds — emails they can impersonate. Used to gate
  // the per-row Impersonate button for non-SuperAdmin actors.
  const [myAllowedEmails, setMyAllowedEmails] = useState([]);

  // Grant-management modal state (SuperAdmin manages another user's grants)
  const [grantsTarget, setGrantsTarget]   = useState(null);   // the actor whose grants we're editing
  const [targetGrants, setTargetGrants]   = useState([]);     // grants belonging to grantsTarget
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [newGrantEmail, setNewGrantEmail] = useState('');
  const [grantsSaving, setGrantsSaving]   = useState(false);
  const [allUsers, setAllUsers]           = useState([]);     // full user list (for dropdown)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.getUsers({ page, limit: 10, role: roleFilter, includeInactive: true });
      setUsers(data.users);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Load the current user's grant list once so we can decide which rows
  // get the Impersonate button. SuperAdmin gets every row.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAPI.listMyImpersonationGrants();
        if (!cancelled) setMyAllowedEmails(data.allowedTargetEmails || []);
      } catch {
        if (!cancelled) setMyAllowedEmails([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Open the per-row grant manager (SuperAdmin only) and load grants for that actor.
  // Also fetches the full users list so the target dropdown shows every option,
  // not just the current paginated page.
  const openGrantsManager = async (u) => {
    setGrantsTarget(u);
    setGrantsLoading(true);
    setNewGrantEmail('');
    try {
      const [grantsRes, usersRes] = await Promise.all([
        authAPI.listImpersonationGrants(u.id),
        authAPI.getUsers({ page: 1, limit: 500, includeInactive: true }),
      ]);
      setTargetGrants(grantsRes.data.grants || []);
      setAllUsers(usersRes.data.users || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load grants.');
      setTargetGrants([]);
      setAllUsers([]);
    } finally {
      setGrantsLoading(false);
    }
  };

  const addGrant = async () => {
    const email = newGrantEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Invalid email format.');
      return;
    }
    if (targetGrants.some(g => g.allowedTargetEmail === email)) {
      toast.error('That grant already exists.');
      return;
    }
    setGrantsSaving(true);
    try {
      const { data } = await authAPI.createImpersonationGrant({
        actorId: grantsTarget.id,
        allowedTargetEmail: email,
      });
      setTargetGrants(prev => [data.grant, ...prev]);
      setNewGrantEmail('');
      toast.success('Grant added.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add grant.');
    } finally {
      setGrantsSaving(false);
    }
  };

  const removeGrant = async (id) => {
    setGrantsSaving(true);
    try {
      await authAPI.deleteImpersonationGrant(id);
      setTargetGrants(prev => prev.filter(g => g.id !== id));
      toast.success('Grant revoked.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke grant.');
    } finally {
      setGrantsSaving(false);
    }
  };

  const openCreate = () => {
    setEditUser(null);
    setForm({ name: '', email: '', password: '', role: 'Recruiter', isActive: true });
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ name: user.name, email: user.email, password: '', role: user.role, isActive: user.isActive });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await authAPI.deleteUser(deleteTarget.id);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!impersonateTarget) return;
    setStartingImpersonation(true);
    try {
      await startImpersonation(impersonateTarget.id, impersonateReason.trim() || undefined);
      toast.success(`Now acting as ${impersonateTarget.name}`);
      setImpersonateTarget(null);
      setImpersonateReason('');
      // Land on the dashboard rendered as the target user.
      window.location.assign('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start impersonation');
    } finally {
      setStartingImpersonation(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editUser) {
        await authAPI.updateUser(editUser.id, form);
        toast.success('User updated!');
        // Optimistic update — immediately reflect changes in the table
        setUsers(prev => prev.map(u =>
          u.id === editUser.id ? { ...u, name: form.name, email: form.email, role: form.role, isActive: form.isActive } : u
        ));
      } else {
        await authAPI.register(form);
        toast.success('User created!');
        load(); // Re-fetch for new user (need server-generated ID + pagination)
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const anyOverlayOpen = modalOpen || !!deleteTarget || !!impersonateTarget || !!grantsTarget;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Users</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{total} total users</p>
        </div>
        {isSuperAdmin() && <Button onClick={openCreate}>+ Add User</Button>}
      </div>

      <Select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="w-full sm:w-48">
        <option value="">All Roles</option>
        <option>SuperAdmin</option><option>RecruiterManager</option><option>Recruiter</option><option>Developer</option>
      </Select>

      <Card>
        {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : users.length === 0 ? (
          <EmptyState icon="👥" title="No users found" message="Add your first user."
            action={<Button onClick={openCreate}>Add User</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  {['User', 'Role', 'Status', 'Last Login', 'Actions'].map(h => (
                    <th key={h} className="text-left text-xs font-medium uppercase tracking-wider pb-3 pr-4 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  // Key on .id (Sequelize UUID)
                  <tr key={u.id} className="transition-colors" style={{ borderTop: '1px solid var(--border-subtle)' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff' }}>
                          {u.name?.[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</p>
                          <p className="truncate max-w-[220px]" style={{ fontSize: 11, color: 'var(--text-secondary)' }} title={u.email}>{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4"><Badge variant={roleBadge[u.role]}>{u.role}</Badge></td>
                    <td className="py-3 pr-4">
                      <Badge variant={u.isActive ? 'success' : 'danger'}>{u.isActive ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="py-3 pr-4">
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isSuperAdmin() && (
                          <ActionPill color="#6366f1" onClick={() => openEdit(u)} title="Edit user">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                            Edit
                          </ActionPill>
                        )}
                        {/* Impersonate visibility:
                            - SuperAdmin actor: any other active user.
                            - Non-SuperAdmin (granted user): any user whose email is
                              in their grant list — including SuperAdmin per the
                              admin policy choice (no role cap).
                            - Always hidden when already inside an impersonation session. */}
                        {u.id !== currentUser?.id && !impersonatedAs && u.isActive && (
                          (isSuperAdmin() || myAllowedEmails.includes(String(u.email || '').toLowerCase())) && (
                            <ActionPill color="#b45309" onClick={() => setImpersonateTarget(u)} title="Impersonate this user">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M17 11l2 2 4-4" />
                              </svg>
                              Impersonate
                            </ActionPill>
                          )
                        )}
                        {/* Manage impersonation grants — SuperAdmin only, never for self,
                            never for other SuperAdmins (they already have full access). */}
                        {isSuperAdmin() && u.id !== currentUser?.id && u.role !== 'SuperAdmin' && (
                          <ActionPill color="#0ea5e9" onClick={() => openGrantsManager(u)} title="Manage impersonation permissions">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            Permissions
                          </ActionPill>
                        )}
                        {isSuperAdmin() && u.id !== currentUser?.id && (
                          <ActionPill color="#ef4444" onClick={() => setDeleteTarget(u)} title="Delete user">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                            Delete
                          </ActionPill>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Pagination page={page} pages={pages} onPage={setPage} />

      {/* ── Impersonation Permissions modal (SuperAdmin manages another user's grants) ── */}
      <Modal isOpen={!!grantsTarget}
             onClose={() => { setGrantsTarget(null); setTargetGrants([]); setNewGrantEmail(''); }}
             title={grantsTarget ? `Impersonation permissions — ${grantsTarget.name}` : 'Impersonation permissions'}
             size="md">
        <div className="space-y-4">
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}>
            Pick the users <strong style={{ color: 'var(--text-primary)' }}>{grantsTarget?.name}</strong> is
            allowed to impersonate. SuperAdmin accounts can never be impersonated by anyone other than another SuperAdmin,
            and are not shown in the list.
          </p>

          {/* Dropdown is built from the in-memory users list, excluding:
              - the actor themselves
              - SuperAdmin users (always uncreachable)
              - users already granted to this actor */}
          {(() => {
            const grantedEmails = new Set(targetGrants.map(g => g.allowedTargetEmail));
            // SuperAdmin is now grantable per admin policy — include them in
            // the dropdown so the granting admin can pick one.
            const candidates = (allUsers || []).filter(u =>
              u.id !== grantsTarget?.id &&
              !grantedEmails.has(String(u.email || '').toLowerCase())
            );
            return (
              <div className="flex items-end gap-2 flex-wrap">
                <Select
                  className="flex-1 min-w-0"
                  label="Add user"
                  value={newGrantEmail}
                  onChange={e => setNewGrantEmail(e.target.value)}
                >
                  <option value="">— Select a user —</option>
                  {candidates.map(u => (
                    <option key={u.id} value={String(u.email).toLowerCase()}>
                      {u.name} ({u.email}) — {u.role}
                    </option>
                  ))}
                </Select>
                <Button onClick={addGrant} loading={grantsSaving} loadingText="Adding…"
                        disabled={!newGrantEmail}>Add</Button>
              </div>
            );
          })()}

          {grantsLoading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : targetGrants.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
              No impersonation permissions yet.
            </p>
          ) : (
            <div className="space-y-2">
              {targetGrants.map(g => {
                const matched = (allUsers || []).find(u => String(u.email || '').toLowerCase() === g.allowedTargetEmail);
                return (
                  <div key={g.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 8,
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 8, padding: '8px 12px',
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                      <span className="truncate" style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }} title={matched?.name || ''}>
                        {matched?.name || '(no user with this email yet)'}
                      </span>
                      <span className="truncate" style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', overflowWrap: 'anywhere', wordBreak: 'break-all' }} title={g.allowedTargetEmail}>
                        {g.allowedTargetEmail}{matched?.role ? ` · ${matched.role}` : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeGrant(g.id)}
                      disabled={grantsSaving}
                      className="text-xs font-medium"
                      style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Revoke
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="secondary"
                    onClick={() => { setGrantsTarget(null); setTargetGrants([]); setNewGrantEmail(''); }}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Impersonation confirmation modal ── */}
      <Modal isOpen={!!impersonateTarget} onClose={() => { setImpersonateTarget(null); setImpersonateReason(''); }} title="Impersonate User" size="sm">
        <div className="space-y-4">
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            You will act as <strong style={{ color: 'var(--text-primary)' }}>{impersonateTarget?.name}</strong> ({impersonateTarget?.role}).
            Every action you take will be logged with both their and your user IDs,
            and {impersonateTarget?.name?.split(' ')[0] || 'they'} will be notified immediately.
          </p>
          <Input
            label="Reason (optional, recorded in audit log)"
            value={impersonateReason}
            onChange={e => setImpersonateReason(e.target.value)}
            placeholder="e.g. Debug missing dashboard widget for user"
          />
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => { setImpersonateTarget(null); setImpersonateReason(''); }}>Cancel</Button>
            <Button
              onClick={handleStartImpersonation}
              loading={startingImpersonation}
              loadingText="Starting…"
              style={{ backgroundColor: '#b45309', borderColor: '#b45309' }}
            >
              Start impersonation
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Delete confirmation modal ── */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete User" size="sm">
        <div className="space-y-4">
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{deleteTarget?.name}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              loading={deleting}
              loadingText="Deleting…"
              style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editUser ? 'Edit User' : 'Add User'} size="sm">
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Full Name *" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} required />
          <Input label="Email *" type="email" value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })} required />
          {!editUser && (
            <Input label="Password *" type="password" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })} required />
          )}
          <Select label="Role *" value={form.role}
            onChange={e => setForm({ ...form, role: e.target.value })}>
            <option>SuperAdmin</option><option>RecruiterManager</option><option>Recruiter</option><option>Developer</option>
          </Select>
          {editUser && (
            <Select label="Status" value={form.isActive.toString()}
              onChange={e => setForm({ ...form, isActive: e.target.value === 'true' })}>
              <option value="true">Active</option><option value="false">Inactive</option>
            </Select>
          )}
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>{editUser ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
